// ImporterSidebar — Phase 4/2 rewrite.
//
// Slimmed from 14 items to a 5-item minimum that matches the exporter
// skeleton shape:
//   Dashboard · Draft LC Review · Supplier Doc Review · Billing · Settings
//
// Dropped items (reachable elsewhere):
//   * Workspace / Templates / Analytics / Notifications / AI Assistance /
//     Content Library / Shipment Timeline — folded into the dashboard
//     recent-activity + stats surfaces per Phase 4/4
//   * Help → moved to top-bar (future polish)
//   * Legacy routes still resolve via the ?section= redirect layer in
//     Phase 4/6, so bookmarks keep working.
import {
  BarChart3,
  FileText,
  ShieldCheck,
  Building2,
  CreditCard,
  LogOut,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";

export type ImporterSidebarSection =
  | "dashboard"
  | "draft-lc"
  | "supplier-docs"
  | "billing";

interface ImporterSidebarProps extends React.ComponentProps<typeof Sidebar> {
  activeSection: ImporterSidebarSection;
  onSectionChange: (section: ImporterSidebarSection) => void;
  user?: { name?: string; email?: string; id?: string; role?: string };
}

export function ImporterSidebar({
  activeSection,
  onSectionChange,
  user: propUser,
  ...props
}: ImporterSidebarProps) {
  const { user: authUser, logout } = useAuth();
  const user = propUser || authUser;
  const displayName =
    propUser?.name || authUser?.full_name || authUser?.username || user?.email;

  const handleLogout = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    await logout();
  };

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <div className="flex items-center gap-3">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-[#B2F273]/10 text-[#B2F273]">
                  <Building2 className="size-4" />
                </div>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="font-semibold">LCopilot</span>
                  <span className="text-xs text-muted-foreground">
                    Importer Portal
                  </span>
                </div>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="px-2">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={activeSection === "dashboard"}
                  onClick={() => onSectionChange("dashboard")}
                  tooltip="Dashboard"
                  className="text-[#EDF5F2]/80 hover:text-white hover:bg-[#00382E] data-[active=true]:bg-[#B2F273]/10 data-[active=true]:text-[#B2F273]"
                >
                  <BarChart3 />
                  <span>Dashboard</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={activeSection === "draft-lc"}
                  onClick={() => onSectionChange("draft-lc")}
                  tooltip="Upload Draft LC"
                  className="text-[#EDF5F2]/80 hover:text-white hover:bg-[#00382E] data-[active=true]:bg-[#B2F273]/10 data-[active=true]:text-[#B2F273]"
                >
                  <FileText />
                  <span>Upload Draft LC</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={activeSection === "supplier-docs"}
                  onClick={() => onSectionChange("supplier-docs")}
                  tooltip="Upload Supplier Docs"
                  className="text-[#EDF5F2]/80 hover:text-white hover:bg-[#00382E] data-[active=true]:bg-[#B2F273]/10 data-[active=true]:text-[#B2F273]"
                >
                  <ShieldCheck />
                  <span>Upload Supplier Docs</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={activeSection === "billing"}
                  onClick={() => onSectionChange("billing")}
                  tooltip="Billing"
                  className="text-[#EDF5F2]/80 hover:text-white hover:bg-[#00382E] data-[active=true]:bg-[#B2F273]/10 data-[active=true]:text-[#B2F273]"
                >
                  <CreditCard />
                  <span>Billing</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        {user && (
          <SidebarMenu>
            <SidebarMenuItem>
              <div className="flex items-center gap-2 px-2 py-1.5 w-full">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                  {displayName?.charAt(0).toUpperCase() ||
                    user.email?.charAt(0).toUpperCase() ||
                    "U"}
                </div>
                <div className="flex flex-col gap-0.5 leading-none flex-1 min-w-0">
                  <span className="truncate font-medium text-sm">
                    {displayName || user.email}
                  </span>
                  <span className="text-xs text-muted-foreground capitalize">
                    {user.role === "admin" ? "Admin" : "Importer"}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="h-8 w-8 p-0"
                  title="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
